import React, { useEffect, useRef, useState } from 'react'
import { GenericException } from "./ui/errors";
import { loadScript, getRandomColor, useRerender, currencyToSymbol } from "./utils/common";
import axios from 'axios';
import { DateView } from './ui/date';
import { objToEl } from './ui/misc';
import { Popup } from './ui/popups';
import { EmptyView, LoadingOverlay, LoadingView } from './ui/status';
import { last } from './utils';
import { addScrolledToBottomEventListener, removeScrolledToBottomEventListener } from './utils/scrolling';
import { useOnScroll } from './ui/utils';
import { EllipsisVertical } from 'lucide-react';


function StripeCheckout({publishable_api_key, client_secret, return_url, stripe_account_id}){

    const ctx = useRef({}).current;
    const rerender = useRerender();
    useEffect(() => {
        loadScript(`https://js.stripe.com/v3/`).then(async () => {
            const stripe = ctx.stripe = window.Stripe(
                publishable_api_key || StripeCheckout.publishable_api_key,
                stripe_account_id ? {stripeAccount: stripe_account_id} : {}
            );

            const appearance = {theme: 'stripe'};
            const elements = ctx.elements = stripe.elements({ appearance, "clientSecret": client_secret });
            const paymentElement = ctx.paymentElement = elements.create(
                "payment", 
                {"layout": "tabs"}
            );
            ctx.el && paymentElement.mount(ctx.el);
        });
        return () => {
            ctx.paymentElement?.unmount();
        }
    }, []);

    async function confirmPayment(){
        if(ctx.is_loading) return;
        ctx.is_loading = true;

        const resp = await ctx.stripe.confirmPayment({
            elements: ctx.elements,
            confirmParams: {
                return_url: return_url || (window.location.origin + "/afterpayment/stripe")
            },
        })
        ctx.is_loading = false;
        resp.error
            ?   ctx.error_message= resp.error.message
            :   ctx.success= true;
        rerender();
    }

    if(ctx.is_loading) return <LoadingView title={"Confirming Payment"} height={"400px"} />
    if(ctx.success) return <EmptyView title="Payment was successful. You can close this and continue" height={500}/>
    return <div>
        {ctx.error_message && <div className='w3-padding w3-red'>{ctx.error_message}</div>}
        <div ref={(el) => {if(el) {ctx.el= el; ctx.paymentElement?.mount(el);}}} />
        <div className="w3-right-align">
            <div className="w3-button w3-black w3-round" onClick={confirmPayment}>Confirm</div>
        </div>
    </div>
}

function UserWallet({wallet}){
    const rerender = wallet.rerender = useRerender();
    return (
        <div key={`${wallet.wallet_for}${wallet.wallet_currency}`} className="w3-flex">
            <div>
                <div onClick={() => Popup.show("Ledger Entries", <WalletLedgerEntries wallet={wallet} />)}>
                    Your <span style={{color: getRandomColor(wallet.wallet_for)}}>
                        {wallet.wallet_for}
                    </span> wallet has <span className="w3-text-green w3-text-large">{wallet.wallet_amount} {currencyToSymbol(wallet.wallet_currency)}</span>
                </div>
                <div>
                    {wallet.data?.stripe_connected_account_charge_enabled 
                        ? <div className='w3-tag w3-green'>Stripe Connected</div> 
                        : null
                    }
                </div>
            </div>
            {
                UserWallet.Options
                ?   <EllipsisVertical size={18} className="w3-margin-left-auto"
                        onClick={(evt) => Popup.showContextMenu(evt.target, <UserWallet.Options wallet={wallet} />)}
                    />
                :   null
            }
        </div>
    );
}



/** Payments */
function WalletLedgerEntries({wallet, endpoint="/api/wallet/ledger"}){
    const [ledger_entries, setLedgerEntries] = useState(null);
    const [has_more, setHasMore] = useState(true);
    const [is_loading, setIsLoading] = useState(null);
    const scrollable_el_ref = useRef(null);
  
    const fetchPreviousLedger = () => {
        if(!has_more || is_loading) {
            return;
        }
        setIsLoading(true);
        axios.get(WalletLedgerEntries.endpoint || "/api/wallet/ledger", {
            "params": {
                "wallet_user_id": wallet.user_id,
                "wallet_for": wallet.wallet_for,
                "wallet_currency": wallet.wallet_currency,
                "before": ledger_entries && last(ledger_entries)?.created_at
            }
        }).then(
            (resp) => {
                if(resp.data.errors){
                  Popup.show("Error", <GenericException ex={resp.data.errors} />);
                  return;
                }
                setLedgerEntries([...(ledger_entries || []), ...(resp.data?.ledger_entries || [])]);
                setHasMore(resp.data.has_more);
            }
        ).finally(
          () => {
            setIsLoading(false);
          }
        );
    }
  
    useEffect(
        () => {
            fetchPreviousLedger();
        }, []
    );

    useOnScroll(
        scrollable_el_ref.current, (percent) => {
            if (percent === 100) fetchPreviousLedger()
        }, [ledger_entries]
    )

    return (
        <div className="tw-px-4 w3-scroll" ref={scrollable_el_ref}>
            {
                is_loading ? <LoadingOverlay /> : null
            }
            <p className='tw-text-md tw-text-black'>Transaction History</p>
            <div className='tw-border tw-rounded-xl tw-mt-2'>
                <div className='tw-flex tw-text-center tw-text-xs tw-font-bold tw-py-4 tw-border-b'>
                    {/* <div className="w3-col s3">From Balance</div> */}
                    <div className="tw-flex-1">Title</div>
                    <div className="tw-flex-1">Date</div>
                    <div className="tw-flex-1">Transaction</div>
                    <div className="tw-flex-1">Balance</div>
                </div>
                <div className='w3-list tw-mt-2'>
                    {
                        ledger_entries
                            ? ledger_entries.map(
                                (entry) => {
                                    return <div className="tw-flex tw-items-center tw-text-center tw-text-xs tw-p-2 tw-border-b" key={entry.ref_id}>
                                        {/* <div className="w3-col s3">{entry.change_from}</div> */}
                                        <div className="tw-flex-1 tw-text-xs tw-text-black-500 tw-capitalize">
                                            {entry.ref_type.replaceAll("_", " ")}
                                            <div className='tw-text-xxs tw-text-black-500'>
                                                {entry.ref_id}
                                            </div>
                                            {
                                                entry.ref_info
                                                ? <div className='tw-text-xxs tw-text-black-500'>{entry.ref_info}</div>
                                                : null
                                            }
                                            {
                                                entry.comment
                                                ? <div className='tw-text-xxs tw-text-gray-500 tw-italic'>{entry.comment}</div>
                                                : null
                                            }
                                        </div>
                                        <div className="tw-flex-1 tw-text-xs tw-text-black-500">
                                            <DateView className="w3-text-black" millis={entry.created_at} />
                                        </div>
                                        <div className={`tw-flex-1 ${entry.diff.split(" ")[0] < 0 ? "tw-text-red-500" : "tw-text-green-500"}`} >{entry.diff}</div>
                                        <div className="tw-flex-1 tw-text-black">{entry.change_to}</div>
                                    </div>
                                })
                            : <EmptyView title="loading..." height="200px" />
                    }
                </div>
            </div>
            {
                !has_more
                    ? <div className="w3-center w3-row w3-bold w3-padding">EOF</div>
                    : is_loading ? <LoadingView height={"40px"} /> : null
            }
      </div>
    )
}



function PaymentTransactionView({payment_transaction}){
    return (
        <div className="w3-row w3-margin-bottom-8 w3-padding-8 w3-border-bottom">
            <div className="w3-flex-row w3-flex-vcenter w3-margin-bottom-16 w3-row">
                <div className="tw-title w3-capitalize">
                    {payment_transaction.status} payment for <span className="w3-bold">{payment_transaction.payment_for}</span>
                </div>
                <div className="w3-margin-left-auto w3-large w3-bold">
                    {payment_transaction.payment_amount}
                </div>
            </div>
            <div className="w3-row w3-text-grey w3-tiny">
                <div className="w3-margin-right w3-left" style={{color: getRandomColor(payment_transaction.payment_instrument)}}>
                    <span className="fas fa-dot-circle"></span> {payment_transaction.payment_instrument}
                </div>
                <DateView millis={payment_transaction.updated_at} />
            </div>
            <div className="w3-row w3-tiny">
                {   
                    payment_transaction.payment_instrument_id
                    ?   <div>Instrument: {payment_transaction.payment_instrument_id}</div>
                    :   null
                }
                <b>Payment data </b> {objToEl(payment_transaction.payment_data)}
                {payment_transaction.payment_response_data ? <div> <b>Payment response </b>{ objToEl(payment_transaction.payment_response_data) } </div>: null}
            </div>
        </div>
    );
}

function PaymentTransactionsView({payment_transactions}){
    return (
        <div className='w3-padding-8 w3-list w3-list-bordered'>
            {
                payment_transactions.length
                ?   payment_transactions.map(
                        (payment_transaction, i) =>
                            <PaymentTransactionView key={i} payment_transaction={payment_transaction}/>
                    )
                :   <div className="w3-center w3-text-grey w3-padding-16">No payment transactions</div>
            }
        </div>
    )
}

export {StripeCheckout, WalletLedgerEntries, UserWallet, PaymentTransactionView, PaymentTransactionsView};
